// Shared helpers for the admin Course view (CourseView, CourseItemPreview, CourseStructureForms).
import { api } from '../../api';

export const ITEM_TYPES = {
  pdf: { icon: '📄', label: 'PDF' },
  audio: { icon: '🎧', label: 'Audio' },
  video: { icon: '🎬', label: 'Video' },
  image: { icon: '🖼️', label: 'Image' },
  text: { icon: '📝', label: 'Text' },
  link: { icon: '🎮', label: 'Game / Link' },
  quiz: { icon: '❓', label: 'Quiz' },
  task: { icon: '✅', label: 'Task' },
  file: { icon: '📎', label: 'File' },
};
// Types that need an uploaded file (or its URL), as in the backend's add/update content checks.
export const FILE_TYPES = ['video', 'pdf', 'audio', 'image'];

export const byOrder = (list) => [...(list || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
export const coins = (v) => Math.max(0, Math.round(Number(v) || 0));
export const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
export const capitalise = (s) => (s ? s[0].toUpperCase() + s.slice(1) : '—');
export const fmtDuration = (sec) => { const s = Math.round(sec); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
export const fmtSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
export const fileNameOf = (url) => { try { return decodeURIComponent(new URL(url).pathname.split('/').pop()); } catch { return ''; } };
export const hostOf = (url) => { try { return new URL(url).host; } catch { return url; } };
export const quizOf = (item) => (item.quizRef && typeof item.quizRef === 'object' ? item.quizRef : null);
// Games added on "Add New Content" keep their link in externalUrl; links added in the structure builder used fileUrl.
export const linkUrlOf = (item) => item.externalUrl || item.fileUrl || '';

export function canPreview(item) {
  if (FILE_TYPES.includes(item.type)) return Boolean(item.fileUrl);
  if (item.type === 'link') return Boolean(linkUrlOf(item));
  return ['quiz', 'text', 'task'].includes(item.type);
}

export function itemDetails(item) {
  const meta = item.metadata || {};
  const parts = [];
  if (item.type === 'quiz') {
    const quiz = quizOf(item);
    const questions = quiz?.questions?.length ?? item.quizData?.questions?.length;
    if (quiz?.title && quiz.title !== item.title) parts.push(quiz.title);
    if (questions != null) parts.push(plural(questions, 'question'));
    if (quiz?.status) parts.push(capitalise(quiz.status));
    if (!quiz && item.quizRef === null) parts.push('Quiz no longer available');
  }
  if (item.type === 'link' && linkUrlOf(item)) parts.push(hostOf(linkUrlOf(item)));
  if (item.type === 'text' && item.textContent) parts.push(item.textContent.length > 90 ? `${item.textContent.slice(0, 90)}…` : item.textContent);
  if (item.type === 'task' && item.taskData?.submissionType) parts.push(`${capitalise(item.taskData.submissionType)} submission`);
  if (meta.pages) parts.push(plural(meta.pages, 'page'));
  if (meta.duration) parts.push(fmtDuration(meta.duration));
  if (meta.fileSize) parts.push(fmtSize(meta.fileSize));
  if (meta.width && meta.height) parts.push(`${meta.width}×${meta.height}`);
  if (!parts.length && item.fileUrl && item.type !== 'link') parts.push(fileNameOf(item.fileUrl));
  return [item.description, ...parts].filter(Boolean).join(' · ');
}

// Private S3 files need a short-lived signed link; anything else is used as it is.
export async function viewableUrl(url) {
  if (!/^https?:\/\//i.test(url || '')) return url;
  try {
    const res = await api.get('/api/v2/lms/admin/courses/files/view-url', { params: { url } });
    return res.data?.data?.url || url;
  } catch {
    return url;
  }
}
